import React, { useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Breadcrumbs, 
  Link, 
  Divider, 
  Paper, 
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid
} from '@mui/material';
import { 
  NavigateNext, 
  Security, 
  Lock, 
  Shield, 
  Verified,
  Business,
  ContactMail,
  Warning
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

function PrivacyPolicy() {
  const { isLight } = useThemeMode();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  return (
    <Box 
      sx={{ 
        pt: { xs: 10, md: 12 }, 
        pb: 8,
        background: isLight 
          ? 'linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)'
          : 'linear-gradient(135deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        minHeight: '100vh'
      }}
    >
      <Container maxWidth="lg">
        {/* Breadcrumb Navigation */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            background: isLight
              ? 'rgba(255, 255, 255, 0.9)'
              : 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(20px)',
            border: isLight
              ? '1px solid rgba(99, 102, 241, 0.1)'
              : '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: 3,
            p: 2
          }}
        >
          <Breadcrumbs 
            separator={<NavigateNext fontSize="small" sx={{ color: isLight ? '#6366f1' : '#10b981' }} />} 
            aria-label="breadcrumb"
          >
            <Link 
              color="inherit" 
              href="/#home"
              sx={{ 
                color: isLight ? '#64748b' : '#94a3b8',
                textDecoration: 'none',
                '&:hover': { color: isLight ? '#6366f1' : '#10b981' }
              }}
            >
              Home
            </Link>
            <Typography sx={{ color: isLight ? '#6366f1' : '#10b981', fontWeight: 600 }}>
              Privacy Policy
            </Typography>
          </Breadcrumbs>
        </Card>
        
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 6,
            p: { xs: 4, md: 6 },
            background: isLight
              ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)'
              : 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
            color: 'white',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 70% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
              `,
              zIndex: 1,
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: 'rgba(255, 255, 255, 0.2)',
                  mr: 3,
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Shield sx={{ color: 'white', fontSize: '2rem' }} />
              </Avatar>
              <Box>
                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 2 }}>
                  ALPHANETIX PRIVACY
                </Typography>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 800,
                    fontSize: { xs: '2rem', md: '3rem' },
                    lineHeight: 1.2,
                    mb: 1
                  }}
                >
                  Privacy Policy
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="h6" sx={{ opacity: 0.95, mb: 2, maxWidth: '800px' }}>
              Your privacy and data security are fundamental to our mission. 
              Learn how we protect, process, and respect your personal information.
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Chip
                icon={<Security sx={{ color: 'white !important' }} />}
                label="Enterprise Security"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
              <Chip
                icon={<Verified sx={{ color: 'white !important' }} />}
                label="GDPR Compliant"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
              <Chip
                icon={<Lock sx={{ color: 'white !important' }} />}
                label="Data Encryption"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Last Updated */}
        <Card
          elevation={0}
          sx={{
            mb: 6,
            background: isLight
              ? 'rgba(99, 102, 241, 0.05)'
              : 'rgba(16, 185, 129, 0.1)',
            border: `1px solid ${isLight ? 'rgba(99, 102, 241, 0.2)' : 'rgba(16, 185, 129, 0.3)'}`,
            borderRadius: 3,
            p: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Warning sx={{ color: isLight ? '#6366f1' : '#10b981', mr: 2, fontSize: '1.5rem' }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 0.5 }}>
                Last Updated: August 10, 2025
              </Typography>
              <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                This policy is effective immediately and applies to all users of our platform and services.
              </Typography>
            </Box>
          </Box>
        </Card>
        
        {/* Introduction */}
        <Card
          elevation={0}
          sx={{
            mb: 6,
            background: isLight
              ? 'rgba(255, 255, 255, 0.9)'
              : 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(20px)',
            border: isLight
              ? '1px solid rgba(99, 102, 241, 0.1)'
              : '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: 3,
            p: 4
          }}
        >
          <Typography variant="h6" sx={{ color: isLight ? '#1e293b' : '#ffffff', mb: 2, fontWeight: 700 }}>
            Our Commitment to Your Privacy
          </Typography>
          <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', fontSize: '1.1rem', lineHeight: 1.7 }}>
            Alphanetix ("we," "our," or "us") is committed to protecting your privacy and maintaining the highest standards of data security. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, 
            use our enterprise integration platform, or engage with our services. We believe transparency is essential to building trust, 
            and this policy reflects our commitment to responsible data practices.
          </Typography>
        </Card>

        {/* Main Content Sections */}
        <Grid container spacing={4}>
          {/* Section 1: Information Collection */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                background: isLight
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(15, 23, 42, 0.8)',
                backdropFilter: 'blur(20px)',
                border: isLight
                  ? '1px solid rgba(99, 102, 241, 0.1)'
                  : '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: 3,
                p: 4
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: isLight ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    mr: 2
                  }}
                >
                  <Business sx={{ color: 'white' }} />
                </Avatar>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff' }}>
                  1. Information We Collect
                </Typography>
              </Box>
              
              <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                We collect information in several ways to provide you with the best possible experience and to deliver our enterprise integration services effectively:
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: isLight ? '#1e293b' : '#ffffff', mb: 2, fontWeight: 600 }}>
                  Information You Provide Directly
                </Typography>
                <Box component="ul" sx={{ pl: 4, '& li': { mb: 2 } }}>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Contact Information:</strong> Name, email address, phone number, company name, job title, and business address
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Business Information:</strong> Industry type, company size, integration requirements, technical specifications
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Account Information:</strong> Username, password, profile preferences, and billing information
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Communication Data:</strong> Messages, feedback, support requests, and survey responses
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: isLight ? '#1e293b' : '#ffffff', mb: 2, fontWeight: 600 }}>
                  Information Collected Automatically
                </Typography>
                <Box component="ul" sx={{ pl: 4, '& li': { mb: 2 } }}>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Technical Information:</strong> IP address, browser type and version, device information, operating system
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Usage Analytics:</strong> Pages visited, time spent on site, click patterns, feature usage statistics
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Performance Data:</strong> System performance metrics, error logs, and platform utilization data
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Cookies & Tracking:</strong> Session cookies, preference cookies, and analytics tracking identifiers
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Section 2: How We Use Information */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                background: isLight
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(15, 23, 42, 0.8)',
                backdropFilter: 'blur(20px)',
                border: isLight
                  ? '1px solid rgba(99, 102, 241, 0.1)'
                  : '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: 3,
                p: 4
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: isLight ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    mr: 2
                  }}
                >
                  <Security sx={{ color: 'white' }} />
                </Avatar>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff' }}>
                  2. How We Use Your Information
                </Typography>
              </Box>
              
              <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                We use your information for legitimate business purposes that enhance your experience and enable us to provide world-class enterprise integration services:
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: isLight ? '#1e293b' : '#ffffff', mb: 2, fontWeight: 600 }}>
                      Service Delivery & Support
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Providing and maintaining our integration platform
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Processing demo requests and customer inquiries
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Delivering technical support and troubleshooting
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Customizing solutions for your business needs
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: isLight ? '#1e293b' : '#ffffff', mb: 2, fontWeight: 600 }}>
                      Communication & Marketing
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Sending product updates and feature announcements
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Sharing relevant industry insights and best practices
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Inviting you to webinars and educational events
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Providing personalized product recommendations
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: isLight ? '#1e293b' : '#ffffff', mb: 2, fontWeight: 600 }}>
                      Platform Improvement
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Analyzing usage patterns to enhance functionality
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Conducting research for new feature development
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Optimizing performance and user experience
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Identifying and preventing security threats
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: isLight ? '#1e293b' : '#ffffff', mb: 2, fontWeight: 600 }}>
                      Legal & Compliance
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Complying with applicable laws and regulations
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Protecting against fraud and security breaches
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Enforcing our terms of service and policies
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Responding to legal requests and investigations
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          {/* Section 3-8: Additional sections */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                background: isLight
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(15, 23, 42, 0.8)',
                backdropFilter: 'blur(20px)',
                border: isLight
                  ? '1px solid rgba(99, 102, 241, 0.1)'
                  : '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: 3,
                p: 4
              }}
            >
              {/* Data Security */}
              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      background: isLight ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      mr: 2
                    }}
                  >
                    <Lock sx={{ color: 'white' }} />
                  </Avatar>
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff' }}>
                    3. Data Security & Protection
                  </Typography>
                </Box>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  We implement industry-leading security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction:
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 3,
                        background: isLight ? '#f8fafc' : 'rgba(16, 185, 129, 0.1)',
                        borderRadius: 2,
                        border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(16, 185, 129, 0.2)'}`,
                        textAlign: 'center'
                      }}
                    >
                      <Shield sx={{ fontSize: '2.5rem', color: isLight ? '#6366f1' : '#10b981', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                        Enterprise Encryption
                      </Typography>
                      <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                        256-bit SSL/TLS encryption for all data in transit and AES-256 encryption for data at rest
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 3,
                        background: isLight ? '#f8fafc' : 'rgba(16, 185, 129, 0.1)',
                        borderRadius: 2,
                        border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(16, 185, 129, 0.2)'}`,
                        textAlign: 'center'
                      }}
                    >
                      <Security sx={{ fontSize: '2.5rem', color: isLight ? '#6366f1' : '#10b981', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                        Access Controls
                      </Typography>
                      <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                        Multi-factor authentication, role-based permissions, and regular access audits
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 3,
                        background: isLight ? '#f8fafc' : 'rgba(16, 185, 129, 0.1)',
                        borderRadius: 2,
                        border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(16, 185, 129, 0.2)'}`,
                        textAlign: 'center'
                      }}
                    >
                      <Verified sx={{ fontSize: '2.5rem', color: isLight ? '#6366f1' : '#10b981', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                        Compliance Standards
                      </Typography>
                      <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                        SOC 2 Type II, GDPR, CCPA, and ISO 27001 compliance with regular third-party audits
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Data Sharing */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  4. Information Sharing & Disclosure
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  We do not sell, rent, or trade your personal information. We may share your information only in the following limited circumstances:
                </Typography>
                
                <Box component="ul" sx={{ pl: 4, '& li': { mb: 2 } }}>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Service Providers:</strong> Trusted third-party vendors who assist us in operating our platform, conducting business, or serving our customers, under strict confidentiality agreements
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Business Partners:</strong> Strategic partners who help deliver integrated solutions, with your explicit consent
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Legal Requirements:</strong> When required by law, court order, or to protect our rights, property, or safety
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with advance notice to affected users
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Your Rights */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  5. Your Privacy Rights & Choices
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  You have significant control over your personal information. Depending on your location, you may exercise the following rights:
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box component="ul" sx={{ pl: 3, '& li': { mb: 1.5 } }}>
                      <Box component="li">
                        <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Access:</strong> Request a copy of your personal data
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Correction:</strong> Update or correct inaccurate information
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Deletion:</strong> Request deletion of your personal data
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box component="ul" sx={{ pl: 3, '& li': { mb: 1.5 } }}>
                      <Box component="li">
                        <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Portability:</strong> Export your data in a structured format
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Opt-out:</strong> Unsubscribe from marketing communications
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Restrict Processing:</strong> Limit how we use your information
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Cookies */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  6. Cookies & Tracking Technologies
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  We use cookies and similar technologies to enhance your experience, analyze usage patterns, and provide personalized content. You can control cookie settings through your browser preferences.
                </Typography>
              </Box>

              {/* International Transfers */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  7. International Data Transfers
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data during international transfers, including Standard Contractual Clauses and adequacy decisions.
                </Typography>
              </Box>

              {/* Changes to Policy */}
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  8. Policy Updates
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                  We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of material changes via email or prominent website notice at least 30 days before the changes take effect.
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Contact Section */}
        <Card
          elevation={0}
          sx={{
            mt: 6,
            mb: 4,
            background: isLight
              ? 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
              : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(15, 23, 42, 0.8) 100%)',
            border: `2px solid ${isLight ? 'rgba(99, 102, 241, 0.2)' : 'rgba(16, 185, 129, 0.3)'}`,
            borderRadius: 3,
            p: 4
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                background: isLight ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                mr: 3
              }}
            >
              <ContactMail sx={{ color: 'white', fontSize: '1.8rem' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                Privacy Questions or Concerns?
              </Typography>
              <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                Our privacy team is here to help you understand and exercise your rights
              </Typography>
            </Box>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                  Privacy Officer
                </Typography>
                <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                  privacy@alphanetix.com
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                  Support Team
                </Typography>
                <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                  support@alphanetix.com
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                  Phone Support
                </Typography>
                <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                  (855) 314-1529
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>
        
        {/* Legal Disclaimer */}
        <Card
          elevation={0}
          sx={{
            background: isLight
              ? 'rgba(255, 193, 7, 0.1)'
              : 'rgba(255, 193, 7, 0.15)',
            border: `1px solid ${isLight ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.4)'}`,
            borderRadius: 3,
            p: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Warning sx={{ color: '#ffc107', mr: 2, mt: 0.5, fontSize: '1.5rem' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                Important Legal Notice
              </Typography>
              <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8', lineHeight: 1.6 }}>
                This privacy policy is designed to comply with major privacy regulations including GDPR, CCPA, and other applicable laws. 
                However, laws vary by jurisdiction and change over time. This document should be reviewed by qualified legal counsel to ensure 
                compliance with all applicable regulations in your specific business context and geographic regions of operation.
              </Typography>
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
}

export default PrivacyPolicy;